import { useEffect, useRef, useState } from "react";
import { Play, Pause, SpeakerHigh, SpeakerSlash } from "@phosphor-icons/react";

interface VideoPlayerProps {
  url: string;
  displayMode?: "viewport" | "background";
  className?: string;
  style?: React.CSSProperties;
}

function parseVideoUrl(url: string) {
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return { provider: "youtube" as const, videoId: ytMatch[1], hash: undefined };
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/);
  if (vimeoMatch) return { provider: "vimeo" as const, videoId: vimeoMatch[1], hash: vimeoMatch[2] };
  return { provider: null, videoId: null, hash: undefined };
}

const mono = "'IBM Plex Mono', monospace";

// iOS/Android bloqueiam autoplay até haver interação; tentamos algumas vezes
// e, se ainda assim não tocar, damos play no primeiro toque/scroll do usuário.
function useAutoplayFallback() {
  const cleanupRef = useRef<(() => void) | null>(null);

  const attach = (tryPlay: () => void) => {
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      tryPlay();
      if (attempts >= 25) clearInterval(interval);
    }, 800);

    const onGesture = () => tryPlay();
    const events: (keyof DocumentEventMap)[] = ["touchstart", "touchend", "click", "scroll", "visibilitychange"];
    events.forEach((e) => document.addEventListener(e, onGesture, { passive: true }));

    cleanupRef.current = () => {
      clearInterval(interval);
      events.forEach((e) => document.removeEventListener(e, onGesture));
    };
  };


  const detach = () => {
    cleanupRef.current?.();
    cleanupRef.current = null;
  };

  useEffect(() => detach, []);

  return { attach, detach };
}

function formatTime(s: number) {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

function useControlsVisibility() {
  const [visible, setVisible] = useState(false);
  const lingerTimer = useRef<any>(null);
  const hintTimer = useRef<any>(null);

  // Chamado quando o player estiver pronto — dispara o hint de 5s
  const triggerHint = () => {
    clearTimeout(hintTimer.current);
    setVisible(true);
    hintTimer.current = setTimeout(() => setVisible(false), 5000);
  };

  const handleMouseEnter = () => {
    clearTimeout(lingerTimer.current);
    clearTimeout(hintTimer.current);
    setVisible(true);
  };

  const handleMouseLeave = () => {
    clearTimeout(lingerTimer.current);
    lingerTimer.current = setTimeout(() => setVisible(false), 2000);
  };

  useEffect(() => {
    return () => {
      clearTimeout(hintTimer.current);
      clearTimeout(lingerTimer.current);
    };
  }, []);

  return { visible, triggerHint, handleMouseEnter, handleMouseLeave };
}

interface ControlsProps {
  playing: boolean;
  muted: boolean;
  progress: number;
  duration: number;
  visible: boolean;
  onPlayPause: () => void;
  onMute: () => void;
  onSeek: (ratio: number) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

function Controls({
  playing,
  muted,
  progress,
  duration,
  visible,
  onPlayPause,
  onMute,
  onSeek,
  onMouseEnter,
  onMouseLeave,
}: ControlsProps) {
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    onSeek((e.clientX - rect.left) / rect.width);
  };

  const btnStyle: React.CSSProperties = {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    color: "rgba(247,246,242,0.9)",
    display: "flex",
    alignItems: "center",
    lineHeight: 1,
  };

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: 80,
        zIndex: 15,
        pointerEvents: "auto",
      }}
    >
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "32px 20px 16px",
          background: "linear-gradient(transparent, rgba(0,0,0,0.55))",
          opacity: visible ? 1 : 0,
          transition: "opacity 500ms ease",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          pointerEvents: visible ? "auto" : "none",
        }}
      >
        {/* Zona de hit da barra — 16px de altura, linha visual de 1px no centro */}
        <div
          onClick={handleSeek}
          style={{
            width: "100%",
            height: 16,
            cursor: "pointer",
            position: "relative",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          {/* Linha visual 1px */}
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              height: 1,
              background: "rgba(247,246,242,0.25)",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                height: "100%",
                width: `${progress * 100}%`,
                background: "rgba(247,246,242,0.9)",
                transition: "width 200ms linear",
              }}
            ></div>
          </div>
        </div>

        {/* Linha inferior */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <span
            style={{
              fontFamily: mono,
              fontSize: 10,
              letterSpacing: "0.08em",
              color: "rgba(247,246,242,0.5)",
              userSelect: "none",
            }}
          >
            {formatTime(progress * duration)} / {formatTime(duration)}
          </span>
          <div style={{ flex: 1 }}></div>
          <button onClick={onPlayPause} style={{ ...btnStyle, marginRight: 16 }}>
            {playing ? <Pause size={18} weight="thin" /> : <Play size={18} weight="thin" />}
          </button>
          <button onClick={onMute} style={btnStyle}>
            {muted ? <SpeakerSlash size={18} weight="thin" /> : <SpeakerHigh size={18} weight="thin" />}
          </button>
        </div>
      </div>
    </div>
  );
}

function VimeoPlayer({ videoId, hash, displayMode }: { videoId: string; hash?: string; displayMode: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const { visible, triggerHint, handleMouseEnter, handleMouseLeave } = useControlsVisibility();
  const { attach, detach } = useAutoplayFallback();

  useEffect(() => {
    const load = () => {
      if (!containerRef.current) return;
      // Construímos o iframe manualmente para evitar a chamada oEmbed
      // (que retorna 404 em vídeos unlisted/privados sem hash).
      const params = new URLSearchParams({
        autoplay: "1",
        muted: "1",
        loop: "1",
        controls: "0",
        background: "0",
        playsinline: "1",
        autopause: "0",
      });
      if (hash) params.set("h", hash);
      const iframe = document.createElement("iframe");
      iframe.src = `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
      iframe.allow = "autoplay; fullscreen; picture-in-picture";
      iframe.allowFullscreen = true;
      iframe.setAttribute("playsinline", "");
      iframe.style.position = "absolute";
      iframe.style.inset = "0";
      iframe.style.width = "100%";
      iframe.style.height = "100%";
      iframe.style.border = "none";
      containerRef.current.innerHTML = "";
      containerRef.current.appendChild(iframe);

      const P = (window as any).Vimeo.Player;
      const p = new P(iframe);
      playerRef.current = p;
      triggerHint();
      p.on("timeupdate", ({ seconds, duration: d }: any) => {
        setProgress(d > 0 ? seconds / d : 0);
        setDuration(d);
      });
      p.on("play", () => setPlaying(true));
      p.on("pause", () => setPlaying(false));
      p.on("volumechange", ({ muted: m }: any) => setMuted(m));
      // Hint dispara quando o player está pronto e tocando
      p.on("play", triggerHint);
      const tryPlay = () => {
        // Só insistimos enquanto o vídeo estiver parado no início
        p.getPaused?.()
          .then((paused: boolean) => {
            if (!paused) return;
            p.setMuted(true).catch(() => {});
            p.play().catch(() => {});
          })
          .catch(() => {
            p.setMuted(true).catch(() => {});
            p.play().catch(() => {});
          });
      };
      p.ready?.().then(() => {
        p.setMuted(true).catch(() => {});
        p.play().catch(() => {});
      }).catch(() => {});
      tryPlay();
      attach(tryPlay);

    };

    if ((window as any).Vimeo?.Player) {
      load();
    } else {
      const s = document.createElement("script");
      s.src = "https://player.vimeo.com/api/player.js";
      s.onload = load;
      document.head.appendChild(s);
    }

    return () => {
      detach();
      playerRef.current?.destroy();
    };
  }, [videoId, hash]);

  const handlePlayPause = () => {
    if (!playerRef.current) return;
    playing ? playerRef.current.pause() : playerRef.current.play();
  };

  const handleMute = () => {
    if (!playerRef.current) return;
    const next = !muted;
    if (!next) {
      playerRef.current.setMuted(false);
      playerRef.current.setVolume(1);
    } else {
      playerRef.current.setMuted(true);
    }
    setMuted(next);
  };

  const handleSeek = (ratio: number) => {
    if (!playerRef.current || duration === 0) return;
    playerRef.current.setCurrentTime(ratio * duration);
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: displayMode === "viewport" ? "100vh" : "100%",
        background: "#000",
        overflow: "hidden",
      }}
    >
      <div ref={containerRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}></div>
      <Controls
        playing={playing}
        muted={muted}
        progress={progress}
        duration={duration}
        visible={visible}
        onPlayPause={handlePlayPause}
        onMute={handleMute}
        onSeek={handleSeek}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
    </div>
  );
}

function YouTubePlayer({ videoId, displayMode }: { videoId: string; displayMode: string }) {
  const divRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const intervalRef = useRef<any>(null);
  const { visible, triggerHint, handleMouseEnter, handleMouseLeave } = useControlsVisibility();
  const { attach, detach } = useAutoplayFallback();

  useEffect(() => {
    const init = () => {
      if (!divRef.current) return;
      playerRef.current = new (window as any).YT.Player(divRef.current, {
        videoId,
        playerVars: {
          autoplay: 1,
          mute: 1,
          loop: 1,
          playlist: videoId,
          controls: 0,
          modestbranding: 1,
          playsinline: 1,
          rel: 0,
          disablekb: 1,
          iv_load_policy: 3,
        },
        events: {
          onReady: (e: any) => {
            e.target.mute();
            e.target.playVideo();
            setDuration(e.target.getDuration());
            setMuted(true);
            triggerHint();
            // iOS pode ignorar o autoplay: tentamos de novo e no primeiro toque
            attach(() => {
              const p = playerRef.current;
              if (!p) return;
              const YT = (window as any).YT?.PlayerState;
              if (YT && p.getPlayerState?.() === YT.PLAYING) return;
              p.mute?.();
              p.playVideo?.();
            });
          },
          onStateChange: (e: any) => {
            const YT = (window as any).YT.PlayerState;
            const isPlaying = e.data === YT.PLAYING;
            setPlaying(isPlaying);
            if (isPlaying) {
              // Hint dispara na primeira vez que o vídeo toca
              triggerHint();

              intervalRef.current = setInterval(() => {
                const p = playerRef.current;
                if (!p) return;
                setProgress(p.getDuration() > 0 ? p.getCurrentTime() / p.getDuration() : 0);
                setDuration(p.getDuration());
                setMuted(p.isMuted());
              }, 500);
            } else {
              clearInterval(intervalRef.current);
            }
          },
        },
      });
    };

    if ((window as any).YT?.Player) {
      init();
    } else {
      const s = document.createElement("script");
      s.src = "https://www.youtube.com/iframe_api";
      (window as any).onYouTubeIframeAPIReady = init;
      document.head.appendChild(s);
    }

    return () => {
      detach();
      clearInterval(intervalRef.current);
      playerRef.current?.destroy?.();
    };
  }, [videoId]);

  const handlePlayPause = () => {
    if (!playerRef.current) return;
    playing ? playerRef.current.pauseVideo() : playerRef.current.playVideo();
  };

  const handleMute = () => {
    if (!playerRef.current) return;
    if (muted) {
      playerRef.current.unMute();
      playerRef.current.setVolume(100);
      setMuted(false);
    } else {
      playerRef.current.mute();
      setMuted(true);
    }
  };

  const handleSeek = (ratio: number) => {
    if (!playerRef.current || duration === 0) return;
    playerRef.current.seekTo(ratio * duration, true);
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: displayMode === "viewport" ? "100vh" : "100%",
        background: "#000",
        overflow: "hidden",
      }}
    >
      <div ref={divRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}></div>
      <div style={{ position: "absolute", inset: 0, zIndex: 5, pointerEvents: "none" }}></div>
      <Controls
        playing={playing}
        muted={muted}
        progress={progress}
        duration={duration}
        visible={visible}
        onPlayPause={handlePlayPause}
        onMute={handleMute}
        onSeek={handleSeek}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
    </div>
  );
}

export function VideoPlayer({ url, displayMode = "viewport" }: VideoPlayerProps) {
  const { provider, videoId, hash } = parseVideoUrl(url);
  if (!provider || !videoId) return null;

  if (provider === "vimeo") {
    return <VimeoPlayer videoId={videoId} hash={hash} displayMode={displayMode} />;
  }
  return <YouTubePlayer videoId={videoId} displayMode={displayMode} />;
}
